import type { LiveExtension, LiveQueueAgent } from '@arcnet/contracts';

/**
 * One state per queue agent, in the order a supervisor asks: are they even on, are they busy,
 * will they be free soon, can they take the next call (30 September 2026). The switch's status
 * and state answer different questions; this is the one answer the page shows.
 */
export type AgentTone = 'ok' | 'warn' | 'danger' | 'info' | 'muted';

/** A time in a state, read at a glance: 4:05, 1:02:03, or 2 d 3 h once it runs past a day. */
export function stateTime(sec: number, fmt: (s: number) => string): string {
  if (sec < 86_400) return fmt(sec);
  const d = Math.floor(sec / 86_400), h = Math.floor((sec % 86_400) / 3_600);
  return h ? `${d} d ${h} h` : `${d} d`;
}

export interface AgentState {
  label: string;
  tone: AgentTone;
  /** Seconds to show beside the label: time in the state, or wrap-up left. */
  secs: number | null;
  /** Would the queue offer them the next call. */
  free: boolean;
  /** For sorting: busiest first, logged out last. */
  rank: number;
}

/**
 * `phoneDown`: the agent's phone is not registered, or registered but not answering the switch's
 * pings. The switch still counts such an agent as available and offers them calls, which cannot
 * ring (owner, 2 October 2026: "this data doesnt seem right esp people that are offline") -- so
 * they are not Ready here, whatever the switch says.
 */
export function agentState(a: LiveQueueAgent, now: number, ext: LiveExtension | undefined, phoneDown = false): AgentState {
  const since = (ms: number | null) => (ms === null ? null : Math.max(0, Math.floor((now - ms) / 1000)));
  if (a.status === 'Logged Out') return { label: 'Logged out', tone: 'muted', secs: null, free: false, rank: 8 };
  // Before a break, a wrap-up or Ready: whatever else is true, a phone that cannot ring is the thing
  // to fix (owner, 2 October 2026: "the on a break phone is offline though"). A call in progress
  // proves the phone works, so it still reads as on a call.
  if (phoneDown && a.state !== 'In a queue call' && ext?.state !== 'on_call') {
    return { label: 'Phone offline', tone: 'danger', secs: null, free: false, rank: 6 };
  }
  if (a.status === 'On Break') return { label: 'On a break', tone: 'warn', secs: since(a.since), free: false, rank: 7 };
  if (a.state === 'In a queue call') return { label: 'On a queue call', tone: 'info', secs: since(a.since), free: false, rank: 1 };
  if (a.state === 'Receiving') return { label: 'Ringing', tone: 'info', secs: null, free: false, rank: 2 };
  if (a.ready_at !== null && a.ready_at > now) {
    return { label: 'Wrap-up', tone: 'warn', secs: Math.ceil((a.ready_at - now) / 1000), free: false, rank: 3 };
  }
  if (ext?.state === 'on_call') return { label: 'On another call', tone: 'info', secs: since(ext.since), free: false, rank: 4 };
  // Held back by the node (agentavailability.ts): Idle means no phone or another call, and the phone
  // was ruled out above -- most likely a call on a device line this page cannot see.
  if (a.state === 'Idle') return { label: 'On another call', tone: 'info', secs: null, free: false, rank: 4 };
  return { label: 'Ready', tone: 'ok', secs: null, free: true, rank: 5 };
}
